import { Volume2 } from "lucide-react";
import { speak } from "@/lib/tts";
import { IDS } from "@/lib/testIds";

export default function LessonCard({ lesson, variant = "basic" }) {
  const accent =
    variant === "advanced"
      ? {
          ring: "hover:border-amber-200",
          chip: "bg-amber-50 text-amber-700",
          letter: "text-amber-600",
          btn: "bg-amber-100 hover:bg-amber-200 text-amber-800",
          btnWord: "bg-amber-500 hover:bg-amber-600 text-white",
        }
      : {
          ring: "hover:border-sky-200",
          chip: "bg-sky-50 text-sky-700",
          letter: "text-sky-500",
          btn: "bg-sky-100 hover:bg-sky-200 text-sky-700",
          btnWord: "bg-sky-500 hover:bg-sky-600 text-white",
        };

  const playSound = () => {
    // TTS can't reliably speak isolated phonic strings ("k", "sh") — it reads
    // them as letter names. Instead, say the letter then the example word so
    // the sound is demonstrated naturally.
    speak(`${lesson.letter}. As in ${lesson.word}. ${lesson.word}.`, { rate: 0.8 });
  };

  const playWord = () => {
    speak(`${lesson.word}. ${lesson.letter} for ${lesson.word}.`);
  };

  return (
    <div
      data-testid={IDS.lesson.card(lesson.id)}
      className={`group bg-white rounded-3xl p-6 border-2 border-slate-100 ${accent.ring} transition-all shadow-[0_8px_30px_rgb(0,0,0,0.03)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] hover:-translate-y-1 duration-300 flex flex-col`}
    >
      <div className="text-6xl text-center mb-2 group-hover:scale-110 transition-transform">
        {lesson.emoji}
      </div>
      <div className={`font-display text-7xl font-semibold text-center ${accent.letter} leading-none`}>
        <button
          type="button"
          onClick={playSound}
          data-testid={`lesson-letter-${lesson.id}`}
          aria-label={`Play letter ${lesson.letter} and its sound`}
          className="hover:scale-105 active:scale-95 transition-transform focus:outline-none focus:ring-4 focus:ring-slate-100 rounded-2xl px-2"
        >
          {lesson.letter}
        </button>
      </div>
      <div className={`mt-3 mx-auto ${accent.chip} px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest`}>
        /{lesson.sound}/
      </div>
      <div className="mt-3 text-center font-display text-2xl font-medium text-slate-800">
        {lesson.word}
      </div>
      {lesson.note && (
        <p className="mt-2 text-xs text-slate-500 text-center leading-relaxed">
          {lesson.note}
        </p>
      )}
      <div className="mt-5 flex gap-2">
        <button
          onClick={playSound}
          data-testid={IDS.lesson.playSound(lesson.id)}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-2.5 ${accent.btn} font-bold text-sm transition-colors`}
        >
          <Volume2 className="w-4 h-4" /> Sound
        </button>
        <button
          onClick={playWord}
          data-testid={IDS.lesson.playWord(lesson.id)}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-2.5 ${accent.btnWord} font-bold text-sm transition-colors shadow-sm hover:shadow-md`}
        >
          <Volume2 className="w-4 h-4" /> Word
        </button>
      </div>
    </div>
  );
}
